import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { Toaster } from 'react-hot-toast';
import { QueryClient, QueryClientProvider } from 'react-query';
import './styles/westfalen.css';

// Pages
import LoginPage from '@/pages/LoginPage';
import AuthCallbackPage from '@/pages/AuthCallbackPage';
import AuthErrorPage from '@/pages/AuthErrorPage';
import DashboardPage from '@/pages/DashboardPage';
import ItemsPage from '@/pages/ItemsPage';
import LocationsPage from '@/pages/LocationsPage';
import TransactionsPage from '@/pages/TransactionsPage';
import UsersPage from '@/pages/UsersPage';
import SettingsPage from '@/pages/SettingsPage';
import CheckoutPage from '@/pages/CheckoutPage';
import SapInfoPage from '@/pages/SapInfoPage';

// Components
import Layout from '@/components/Layout';
import ProtectedRoute from '@/components/ProtectedRoute';
import SuperAdminRoute from '@/components/SuperAdminRoute';
import AdminRoute from '@/components/AdminRoute';
import { useAuthStore } from '@/stores/authStore';
import { useThemeStore } from '@/stores/themeStore';

// Create a client
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 1,
      refetchOnWindowFocus: false,
    },
  },
});

function App() {
  const { isAuthenticated } = useAuthStore();
  const { theme } = useThemeStore();

  return (
    <QueryClientProvider client={queryClient}>
      <div className={`min-h-screen ${theme === 'dark' ? 'dark' : ''}`}>
        <Router>
          <Routes>
            {/* Public routes */}
            <Route 
              path="/login" 
              element={
                isAuthenticated ? <Navigate to="/dashboard" replace /> : <LoginPage />
              } 
            />
            <Route path="/auth/callback" element={<AuthCallbackPage />} />
            <Route path="/auth/error" element={<AuthErrorPage />} />
            
            {/* Checkout route - accessible without authentication in demo mode */}
            <Route path="/checkout/:itemId" element={<CheckoutPage />} />
            
            {/* Protected routes */}
            <Route path="/" element={<ProtectedRoute />}>
              <Route element={<Layout />}>
                <Route index element={<Navigate to="/dashboard" replace />} />
                <Route path="dashboard" element={<DashboardPage />} />
                <Route 
                  path="items" 
                  element={
                    <AdminRoute>
                      <ItemsPage />
                    </AdminRoute>
                  } 
                />
                <Route 
                  path="locations" 
                  element={
                    <AdminRoute>
                      <LocationsPage />
                    </AdminRoute>
                  } 
                />
                <Route path="transactions" element={<TransactionsPage />} />
                <Route 
                  path="users" 
                  element={
                    <AdminRoute>
                      <UsersPage />
                    </AdminRoute>
                  } 
                />
                <Route 
                  path="sap-info" 
                  element={
                    <AdminRoute>
                      <SapInfoPage />
                    </AdminRoute>
                  } 
                />
                <Route 
                  path="settings" 
                  element={
                    <SuperAdminRoute>
                      <SettingsPage />
                    </SuperAdminRoute>
                  } 
                />
              </Route>
            </Route>

            {/* 404 route */}
            <Route path="*" element={<Navigate to="/dashboard" replace />} />
          </Routes>
        </Router>

        {/* Toast notifications */}
        <Toaster
          position="top-right"
          toastOptions={{
            duration: 4000,
            style: {
              background: theme === 'dark' ? '#1f2937' : '#ffffff',
              color: theme === 'dark' ? '#f9fafb' : '#111827',
              border: theme === 'dark' ? '1px solid #374151' : '1px solid #e5e7eb',
            },
          }}
        />
      </div>
    </QueryClientProvider>
  );
}

export default App;
